import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../../services/API'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MAX_RECIPIENTS = 10

// Emails ward/department contacts (plus any typed addresses) an introduction
// to the hospital booking portal — the fix for "we didn't know it existed".
const AnnouncePortalDialog = ({ organisation, open, onClose }) => {
  const [wardContacts, setWardContacts] = useState([])
  const [selectedEmails, setSelectedEmails] = useState([])
  const [extraRecipients, setExtraRecipients] = useState([])
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [sentCount, setSentCount] = useState(null)

  useEffect(() => {
    if (!open || !organisation?._id) return
    setError(null)
    setSentCount(null)
    setSelectedEmails([])
    setExtraRecipients([])
    setMessage('')
    setLoading(true)
    API.get(`/pts/organisations/${organisation._id}/wards`)
      .then((res) => {
        if (res.status === 200) {
          const contacts = (res.data || [])
            .filter((ward) => EMAIL_PATTERN.test(ward?.contactEmail || ''))
            .map((ward) => ({
              name: ward.contactName || ward.name,
              email: ward.contactEmail.toLowerCase(),
              wardName: [ward.name, ward.internalLocation]
                .filter(Boolean)
                .join(' - '),
            }))
          // De-duplicate shared inboxes across wards
          const seen = new Set()
          setWardContacts(
            contacts.filter((contact) => {
              if (seen.has(contact.email)) return false
              seen.add(contact.email)
              return true
            }),
          )
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [open, organisation?._id])

  const addExtraRecipient = () => {
    const email = newEmail.trim().toLowerCase()
    if (!EMAIL_PATTERN.test(email)) {
      setError('Enter a valid email address.')
      return
    }
    setError(null)
    setExtraRecipients((prev) =>
      prev.some((recipient) => recipient.email === email)
        ? prev
        : [...prev, { name: newName.trim(), email }],
    )
    setNewName('')
    setNewEmail('')
  }

  const recipients = [
    ...wardContacts.filter((contact) => selectedEmails.includes(contact.email)),
    ...extraRecipients,
  ]

  const send = async () => {
    setSending(true)
    setError(null)
    try {
      const res = await API.post(
        `/pts/organisations/${organisation._id}/announce-portal`,
        {
          recipients: recipients.map(({ name, email }) => ({ name, email })),
          message,
        },
      )
      if (res.status === 200) {
        setSentCount(res.data?.sent ?? recipients.length)
      } else {
        setError(res.data?.error || 'Unable to send the announcement.')
      }
    } catch (e) {
      setError(e?.response?.data?.error || 'Unable to send the announcement.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onClose={sending ? null : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Announce the portal — {organisation?.orgName}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {sentCount !== null ? (
          <Alert severity="success">
            Announcement sent to {sentCount} recipient
            {sentCount === 1 ? '' : 's'}.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Sends an introduction to the hospital booking portal with a login
              link. Pick ward contacts and/or add other recipients (up to{' '}
              {MAX_RECIPIENTS}).
            </Typography>

            {wardContacts.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Ward contacts</Typography>
                {wardContacts.map((contact) => (
                  <FormControlLabel
                    key={contact.email}
                    control={
                      <Checkbox
                        checked={selectedEmails.includes(contact.email)}
                        onChange={(e) =>
                          setSelectedEmails((prev) =>
                            e.target.checked
                              ? [...prev, contact.email]
                              : prev.filter((email) => email !== contact.email),
                          )
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {contact.name} — {contact.email}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {' '}
                          ({contact.wardName})
                        </Typography>
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            )}
            {!loading && wardContacts.length === 0 && (
              <Alert severity="info">
                No ward contacts with email addresses are recorded for this
                organisation — add recipients below instead.
              </Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Name"
                size="small"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <TextField
                label="Email"
                size="small"
                fullWidth
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addExtraRecipient()
                  }
                }}
              />
              <Button onClick={addExtraRecipient} sx={{ flexShrink: 0 }}>
                Add
              </Button>
            </Stack>
            {extraRecipients.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {extraRecipients.map((recipient) => (
                  <Chip
                    key={recipient.email}
                    label={recipient.name || recipient.email}
                    onDelete={() =>
                      setExtraRecipients((prev) =>
                        prev.filter((r) => r.email !== recipient.email),
                      )
                    }
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Stack>
            )}

            <TextField
              label="Add a message (optional)"
              multiline
              minRows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={sending}>
          {sentCount !== null ? 'Close' : 'Cancel'}
        </Button>
        {sentCount === null && (
          <Button
            variant="contained"
            onClick={send}
            disabled={
              sending || !recipients.length || recipients.length > MAX_RECIPIENTS
            }
          >
            {sending
              ? 'Sending...'
              : `Send to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AnnouncePortalDialog
